import { useCallback, useEffect, useRef, useState } from "react";
import { useQueryClient } from "react-query";
import { useToast } from "@chakra-ui/react";
import { taskService } from "api/ai/ai.service";
import { postFoundationExtract } from "api/ai/foundationExtract";
import FoundationDataV2Api from "api/issb/foundationDataV2";
import { useAuth } from "context";
import { foundationV2Keys } from "hooks/query";
import { showErrorToast } from "utils";
import {
  extensionOf,
  hasOpenRun,
  SOURCE_EXTENSIONS,
  SOURCE_EXTENSIONS_LABEL,
} from "utils/foundationSourceRuns";

/**
 * Starting, polling and cancelling one workstream's extractions.
 *
 * The run row in Java is the source of truth for status; the AI service's task is only the live progress view.
 * So this polls the runs list for the outcome and leaves `AIProgressWidget` to show the percentage — if the
 * task were the only signal, closing the tab mid-run would lose the result.
 *
 * @param {number} dataYearId the workstream's row id — what runs hang off
 * @param {number} year the calendar year, which the extraction prompt states to the model
 * @param {Array} runs
 * @param {Function} onRunsChanged invalidate whatever a finished run wrote
 */
export default function useSourceExtraction({ dataYearId, year, runs, onRunsChanged }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const { callApi } = useAuth();

  const [startingFileId, setStartingFileId] = useState(null);
  /*
    Per file, not one id. The run row only learns its job id when the worker picks the task up and the
    TaskRunner polls on an interval, so agentJobId is null for the first seconds after enqueue — and with
    several sources a single shared id would attribute one file's progress to another.
  */
  const [startedTaskIds, setStartedTaskIds] = useState({});

  const onError = (err) =>
    showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message);

  const isRunning = hasOpenRun(runs);

  /* Poll while ANY run is open — several sources can be in flight, not just the newest. */
  useEffect(() => {
    if (!isRunning || !dataYearId) return undefined;
    const timer = setInterval(() => {
      queryClient.invalidateQueries(foundationV2Keys.runs(dataYearId));
    }, 5000);
    return () => clearInterval(timer);
  }, [isRunning, dataYearId, queryClient]);

  /*
    When any run closes, pull in whatever it wrote. Tracked per run rather than with one "last seen status",
    which could only ever notice the newest.
  */
  const lastSeenStatuses = useRef(new Map());
  useEffect(() => {
    if (!runs) return;
    let closed = false;
    runs.forEach((run) => {
      const previous = lastSeenStatuses.current.get(run.id);
      lastSeenStatuses.current.set(run.id, run.status);
      if (previous && previous !== run.status && !["QUEUED", "RUNNING"].includes(run.status)) {
        closed = true;
      }
    });
    if (closed) onRunsChanged?.();
  }, [runs, onRunsChanged]);

  /** The task id to poll for one file: the durable value first, the enqueue's answer as a fallback. */
  const taskIdFor = useCallback(
    (fileId, run) => run?.agentJobId || startedTaskIds[String(fileId)] || null,
    [startedTaskIds]
  );

  /**
   * Registers a run for one document and enqueues the job.
   *
   * Two calls on purpose: the run row exists — and is visible in history — even if the enqueue then fails,
   * rather than the work vanishing with no record that it was attempted.
   *
   * @returns {Promise<number|null>} the run id, so a caller can open its review when it finishes
   */
  const startExtraction = useCallback(
    async (fileInfo) => {
      if (!fileInfo || !dataYearId) return null;
      const extension = extensionOf(fileInfo.filename || fileInfo.title);
      if (!SOURCE_EXTENSIONS.includes(extension)) {
        /*
          Refused before a run row exists. There is no OCR or vision path in any service, so an image can
          never succeed and a queued run failing minutes later tells the user nothing useful.
        */
        showErrorToast(
          toast,
          `Cannot extract from .${extension || "?"} files — use ${SOURCE_EXTENSIONS_LABEL}.`
        );
        return null;
      }

      setStartingFileId(String(fileInfo.id));
      let createdRunId = null;
      try {
        const run = await callApi(FoundationDataV2Api.createRun, {
          dataYearId,
          sourceFileInfoId: fileInfo.id,
        });
        createdRunId = run.id;
        const task = await postFoundationExtract({
          runId: run.id,
          year,
          fileinfoId: fileInfo.id,
        });
        if (task?.id) {
          setStartedTaskIds((prev) => ({ ...prev, [String(fileInfo.id)]: task.id }));
          try {
            // Persist it so a reload before the worker starts can still poll and cancel. Non-fatal: the
            // worker reports the same id itself when it picks the task up.
            await callApi(FoundationDataV2Api.reportRunJob, {
              runId: run.id,
              agentJobId: task.id,
              agentService: "esgai",
            });
          } catch (err) {
            console.warn("could not record the extraction job id on the run", err);
          }
        }
        queryClient.invalidateQueries(foundationV2Keys.runs(dataYearId));
        return createdRunId;
      } catch (err) {
        onError(err);
        /*
          The run row was created before the enqueue, so a failed enqueue would otherwise leave it QUEUED
          forever with no task behind it — unpollable, uncancellable, and indistinguishable from a job merely
          slow to start. Close it with the reason instead.
        */
        if (createdRunId) {
          try {
            await callApi(FoundationDataV2Api.closeRun, {
              runId: createdRunId,
              status: "FAILED",
              error: `Could not start the extraction job: ${err.response?.data?.detail ?? err.message}`,
            });
            queryClient.invalidateQueries(foundationV2Keys.runs(dataYearId));
          } catch (closeErr) {
            console.warn("could not close the run after a failed enqueue", closeErr);
          }
        }
        return null;
      } finally {
        setStartingFileId(null);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [callApi, dataYearId, year, queryClient, toast]
  );

  const cancel = useCallback(
    async (run) => {
      if (!run) return;
      const taskId = run.agentJobId || null;
      try {
        /*
          Stop the job first where there is one, so the worker sees the cancellation at its next checkpoint and
          cannot post a result after we have closed the run. Then close the run regardless — a run whose
          enqueue never succeeded has no task at all, and Cancel still has to work for it.
        */
        if (taskId) {
          try {
            await taskService.cancel(taskId);
          } catch (taskErr) {
            console.warn("could not cancel the AI task; closing the run anyway", taskErr);
          }
        }
        await callApi(FoundationDataV2Api.closeRun, {
          runId: run.id,
          status: "CANCELLED",
          error: null,
        });
        queryClient.invalidateQueries(foundationV2Keys.runs(dataYearId));
      } catch (err) {
        onError(err);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [callApi, dataYearId, queryClient]
  );

  return { startExtraction, cancel, isRunning, startingFileId, taskIdFor };
}
